import log from '@/lib/logger';
import {
  deleteEmailSubscription,
  deleteInAppSubscription,
  getEmailSubscriptions,
  getInAppNotifications,
  getInAppSubscriptions,
  getNotificationActions,
  patchInAppNotification,
  postEmailSubscription,
  postInAppSubscription,
} from '@/services/notifications';
import { type RootState } from '@/store';
import { RequestStatus } from '@/types/client';
import type { ActionByCategory, Notification, SubscriptionCreateDelete } from '@/types/swagger';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'sonner';

export interface NotificationsState {
  expoPushToken: { token: string | null; error?: string | null };
  notifications: Notification[];
  notificationActions: ActionByCategory[];
  inAppSubscriptions: SubscriptionCreateDelete[];
  emailSubscriptions: SubscriptionCreateDelete[];
  fetching: {
    notifications: RequestStatus;
    actions: RequestStatus;
    inAppSubscriptions: RequestStatus;
    emailSubscriptions: RequestStatus;
  };
  page: number;
  unseenCount: number;
}

const initialState: NotificationsState = {
  expoPushToken: { token: null, error: null },
  notifications: [],
  notificationActions: [],
  inAppSubscriptions: [],
  emailSubscriptions: [],
  fetching: {
    notifications: RequestStatus.IDLE,
    actions: RequestStatus.IDLE,
    inAppSubscriptions: RequestStatus.IDLE,
    emailSubscriptions: RequestStatus.IDLE,
  },
  page: 1,
  unseenCount: 0,
};

export const fetchNotificationActions = createAsyncThunk(
  'notifications/fetchNotificationActions',
  async (_, thunkAPI) => {
    try {
      const data = await getNotificationActions();
      return data.results;
    } catch (error) {
      log.error('Failed to fetch notification actions:', error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const fetchInAppSubscriptions = createAsyncThunk(
  'notifications/fetchSubscriptions',
  async () => {
    const response = await getInAppSubscriptions();
    if (response.status !== 200) {
      throw new Error('Failed to fetch in app subscriptions');
    }
    return response.data.results;
  }
);

export const fetchEmailSubscriptions = createAsyncThunk(
  'notifications/fetchEmailSubscriptions',
  async () => {
    const response = await getEmailSubscriptions();
    if (response.status !== 200) {
      throw new Error('Failed to fetch email subscriptions');
    }
    return response.data.results;
  }
);

export const fetchNotifications = createAsyncThunk(
  'notifications/lazyLoadNotifications',
  async (
    {
      page,
      pageSize,
      initial,
    }: { page?: number; pageSize?: number; lazy?: boolean; initial?: boolean },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as RootState;
    if (state.notifications.page === -1) return { notifications: [], initial };
    const response = await getInAppNotifications(page, pageSize);
    if (response.status !== 200) {
      throw new Error('Failed to fetch in app notifications');
    }
    thunkAPI.dispatch(setNotificationsPage(response.data.next ? state.notifications.page + 1 : -1));
    return { notifications: response.data.results, initial };
  }
);

export const markNotificationAsSeen = createAsyncThunk(
  'notifications/markNotificationAsSeen',
  async (id: number) => {
    const response = await patchInAppNotification(id);
    if (response.status !== 200) throw new Error('Failed to mark notification as seen');
    return response.data;
  }
);

export const removeInAppSubscriptions = createAsyncThunk(
  'notifications/removeInAppSubscriptions',
  async (subscriptions: SubscriptionCreateDelete[]) => {
    const response = await deleteInAppSubscription(subscriptions);
    if (response.status !== 204) toast.error('Failed to remove in app subscription');
    return subscriptions.map((s) => s.action_code);
  }
);

export const createInAppSubscriptions = createAsyncThunk(
  'notifications/createInAppSubscriptions',
  async (subscriptions: SubscriptionCreateDelete[]) => {
    const response = await postInAppSubscription(subscriptions);
    if (response.status !== 201) toast.error('Failed to create in app subscription');
    return response.data;
  }
);
export const removeEmailSubscriptions = createAsyncThunk(
  'notifications/removeEmailSubscriptions',
  async (subscriptions: SubscriptionCreateDelete[]) => {
    const response = await deleteEmailSubscription(subscriptions);
    if (response.status !== 204) toast.error('Failed to remove email subscription');
    return subscriptions.map((s) => s.action_code);
  }
);

export const createEmailSubscriptions = createAsyncThunk(
  'notifications/createEmailSubscriptions',
  async (subscriptions: SubscriptionCreateDelete[]) => {
    const response = await postEmailSubscription(subscriptions);
    if (response.status !== 201) toast.error('Failed to create email subscription');
    return response.data;
  }
);

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    resetNotificationsStore: (_state) => (_state = initialState),
    setNotificationActions: (state, action) => {
      state.notificationActions = action.payload;
    },
    setInAppSubscriptions: (state, action) => {
      state.inAppSubscriptions = action.payload;
    },
    setEmailSubscriptions: (state, action) => {
      state.emailSubscriptions = action.payload;
    },
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload;
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.unseenCount++;
      state.notifications.unshift(action.payload);
    },
    setNotificationsPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setUnseenNotificationsCount: (state, action: PayloadAction<number>) => {
      state.unseenCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationActions.pending, (state) => {
        state.fetching.actions = RequestStatus.FETCHING;
      })
      .addCase(fetchNotificationActions.fulfilled, (state, action) => {
        state.fetching.actions = RequestStatus.SUCCESS;
        state.notificationActions = action.payload;
      })
      .addCase(fetchNotificationActions.rejected, (state) => {
        state.fetching.actions = RequestStatus.ERROR;
      });
    builder
      .addCase(fetchInAppSubscriptions.pending, (state) => {
        state.fetching.inAppSubscriptions = RequestStatus.FETCHING;
      })
      .addCase(fetchInAppSubscriptions.fulfilled, (state, action) => {
        state.fetching.inAppSubscriptions = RequestStatus.SUCCESS;
        state.inAppSubscriptions = action.payload;
      })
      .addCase(fetchInAppSubscriptions.rejected, (state) => {
        state.fetching.inAppSubscriptions = RequestStatus.ERROR;
      });
    builder
      .addCase(fetchEmailSubscriptions.pending, (state) => {
        state.fetching.emailSubscriptions = RequestStatus.FETCHING;
      })
      .addCase(fetchEmailSubscriptions.fulfilled, (state, action) => {
        state.fetching.emailSubscriptions = RequestStatus.SUCCESS;
        state.emailSubscriptions = action.payload;
      })
      .addCase(fetchEmailSubscriptions.rejected, (state) => {
        state.fetching.emailSubscriptions = RequestStatus.ERROR;
      });
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.fetching.notifications = RequestStatus.FETCHING;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { notifications, initial } = action.payload;
        state.notifications = initial ? notifications : [...state.notifications, ...notifications];
        state.fetching.notifications = RequestStatus.SUCCESS;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.fetching.notifications = RequestStatus.ERROR;
      });

    builder
      .addCase(markNotificationAsSeen.pending, () => {})
      .addCase(markNotificationAsSeen.fulfilled, (state, action) => {
        const notification = state.notifications.find((n) => n.id === action.payload.id);
        if (notification) {
          notification.seen = true;
          state.unseenCount--;
        }
      })
      .addCase(markNotificationAsSeen.rejected, () => {});

    builder.addCase(createInAppSubscriptions.fulfilled, (state, action) => {
      state.inAppSubscriptions = [...state.inAppSubscriptions, ...action.payload];
    });
    builder.addCase(removeInAppSubscriptions.fulfilled, (state, action) => {
      state.inAppSubscriptions = state.inAppSubscriptions.filter(
        (subscription) => !action.payload.includes(subscription.action_code)
      );
    });
    builder.addCase(createEmailSubscriptions.fulfilled, (state, action) => {
      state.emailSubscriptions = [...state.emailSubscriptions, ...action.payload];
    });
    builder.addCase(removeEmailSubscriptions.fulfilled, (state, action) => {
      state.emailSubscriptions = state.emailSubscriptions.filter(
        (subscription) => !action.payload.includes(subscription.action_code)
      );
    });
  },
});

export const {
  resetNotificationsStore,
  setNotifications,
  addNotification,
  setNotificationActions,
  setInAppSubscriptions,
  setEmailSubscriptions,
  setNotificationsPage,
  setUnseenNotificationsCount,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
